// Ключ устройства на WebCrypto (ADR-031): Ed25519 основной, ECDSA P-256 — там, где Ed25519 не поддержан
// (Electron ≤ 36 по spike Р-03). Ключ создаётся непереносимым (`extractable = false`): приватную часть
// нельзя вытащить ни из кода ядра, ни из консоли браузера — наружу уходит только подпись.

import type { Bytes } from "./bytes.ts";
import type { DeviceSigner, KeyAlgorithm } from "./signing.ts";

/** Параметры WebCrypto по алгоритму. */
const params = {
  ED25519: { name: "Ed25519" },
  ECDSA_P256: { name: "ECDSA", namedCurve: "P-256" },
} as const;

/** Подпись: у P-256 хеш задаётся при подписании, у Ed25519 — нет. */
const signParams = {
  ED25519: { name: "Ed25519" },
  ECDSA_P256: { name: "ECDSA", hash: "SHA-256" },
} as const;

/** Ключ устройства поверх пары WebCrypto. */
export class WebCryptoSigner implements DeviceSigner {
  readonly algorithm: KeyAlgorithm;
  readonly #pair: CryptoKeyPair;

  constructor(algorithm: KeyAlgorithm, pair: CryptoKeyPair) {
    this.algorithm = algorithm;
    this.#pair = pair;
  }

  async sign(canonical: Bytes): Promise<Bytes> {
    const signature = await crypto.subtle.sign(signParams[this.algorithm], this.#pair.privateKey, canonical);
    return new Uint8Array(signature);
  }

  async publicKey(): Promise<Bytes> {
    // Ed25519 — 32 байта «raw», P-256 — несжатая точка SEC1 (65 байт): в обоих случаях ровно то,
    // что ждёт сервер в `PublicKey.key`.
    const raw = await crypto.subtle.exportKey("raw", this.#pair.publicKey);
    return new Uint8Array(raw);
  }

  /** Пара ключей — для сохранения в хранилище платформы (IndexedDB принимает CryptoKey как есть). */
  get pair(): CryptoKeyPair {
    return this.#pair;
  }
}

/** Поддерживает ли среда Ed25519: в Electron ≤ 36 и старых браузерах — нет (spike Р-03). */
export async function supportsEd25519(): Promise<boolean> {
  try {
    const pair = (await crypto.subtle.generateKey(params.ED25519, false, ["sign", "verify"]));
    return pair.privateKey !== undefined;
  } catch {
    return false;
  }
}

/**
 * Новый ключ устройства: Ed25519, а если среда его не умеет — P-256. Непереносимый: `extractable = false`
 * относится к приватной части, публичную экспортировать можно (WebCrypto хранит её отдельным ключом).
 */
export async function createDeviceKey(preferred?: KeyAlgorithm): Promise<WebCryptoSigner> {
  const algorithm: KeyAlgorithm = preferred ?? ((await supportsEd25519()) ? "ED25519" : "ECDSA_P256");
  const pair = (await crypto.subtle.generateKey(params[algorithm], false, ["sign", "verify"])) as CryptoKeyPair;
  return new WebCryptoSigner(algorithm, pair);
}

/** Ключ из сохранённой пары (из IndexedDB). */
export function deviceKeyFromPair(algorithm: KeyAlgorithm, pair: CryptoKeyPair): WebCryptoSigner {
  return new WebCryptoSigner(algorithm, pair);
}

/** Проверка чужой подписи — нужна conformance-тесту и отладке; в рабочем пути клиента не используется. */
export async function verifySignature(
  algorithm: KeyAlgorithm,
  publicKey: Bytes,
  canonical: Bytes,
  signature: Bytes,
): Promise<boolean> {
  const key = await crypto.subtle.importKey("raw", publicKey, params[algorithm], false, ["verify"]);
  return crypto.subtle.verify(signParams[algorithm], key, signature, canonical);
}
